import type { Agent } from '@atproto/api'
import {
    BSKY_PROFILE_COLLECTION,
    blueskyLinksFromViews,
    hostnameLabel,
    isSpecificPlatformIcon,
    mergeProfileLinks,
    resolveBlueskyLinkRecords,
    resolvedToProfileLinks,
    rkeyFromAtUri,
    type ProfileLink,
    type ProfileLinkRepo,
} from '@mackinac/shared'

/** GraphQL / profile shape for one chip. Extra keys are omitted when absent. */
export interface AtLinkedAccount {
    rkey: string
    icon: string
    name: string
    link: string
    order: number
    source?: ProfileLink['source']
    iconUrl?: string
    uri?: string
    cid?: string
    verifiedAt?: string
}

export function profileLinksToAccounts(links: ProfileLink[]): AtLinkedAccount[] {
    return links.map((link, index) => {
        const account: AtLinkedAccount = {
            rkey: link.uri ? (rkeyFromAtUri(link.uri) ?? `bsky-${index}`) : `embedded-${index}`,
            icon: isSpecificPlatformIcon(link.platformIcon) ? link.platformIcon! : 'link',
            name: link.title.trim() || hostnameLabel(link.url),
            link: link.url,
            order: index,
            source: link.source,
        }
        if (link.iconUrl) account.iconUrl = link.iconUrl
        if (link.uri) account.uri = link.uri
        if (link.cid) account.cid = link.cid
        if (link.verifiedAt) account.verifiedAt = link.verifiedAt
        return account
    })
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

type RawLinks = { kind: 'has'; links: unknown } | { kind: 'absent' } | { kind: 'failed' }

/**
 * The installed `@atproto/api` lexicon may not know `links` yet and can drop
 * the field. A second read of the same XRPC method keeps the JSON the AppView
 * actually sent. Skipped when the agent has no service URL (unit-test doubles).
 */
async function rawGetProfileLinks(agent: Agent, actor: string): Promise<RawLinks> {
    const service = (agent as { serviceUrl?: URL }).serviceUrl
    if (!service || !actor) return { kind: 'failed' }
    try {
        const url = new URL('/xrpc/app.bsky.actor.getProfile', service)
        url.searchParams.set('actor', actor)
        const response = await fetch(url.toString())
        if (!response.ok) return { kind: 'failed' }
        const json: unknown = await response.json()
        if (!isPlainObject(json) || !('links' in json)) return { kind: 'absent' }
        return { kind: 'has', links: json.links }
    } catch {
        return { kind: 'failed' }
    }
}

function readRepo(agent: Agent): ProfileLinkRepo | null {
    const getRecord = agent.com?.atproto?.repo?.getRecord
    if (typeof getRecord !== 'function') return null
    return {
        async getRecord(args) {
            const response = await getRecord.call(agent.com.atproto.repo, args)
            return { cid: response.data.cid, value: response.data.value }
        },
        putRecord() {
            throw new Error('profile link reads do not write')
        },
        createRecord() {
            throw new Error('profile link reads do not write')
        },
        deleteRecord() {
            return Promise.resolve()
        },
    }
}

async function linksFromProfileRecord(
    agent: Agent,
    did: string,
    profileRecord: Record<string, unknown> | undefined
): Promise<ProfileLink[]> {
    const repo = readRepo(agent)
    let value = profileRecord
    if (!Array.isArray(value?.links)) {
        if (!repo) return []
        try {
            const record = await repo.getRecord({
                repo: did,
                collection: BSKY_PROFILE_COLLECTION,
                rkey: 'self',
            })
            value = isPlainObject(record?.value) ? record.value : undefined
        } catch {
            return []
        }
    }
    if (!repo || !value) return []
    try {
        const resolved = await resolveBlueskyLinkRecords(repo, did, value)
        return resolvedToProfileLinks(resolved, did)
    } catch {
        return []
    }
}

/**
 * Prefer AppView `getProfile.links`. When that field is absent, resolve the
 * profile record's strongRefs (https, shortener list, same DID, first 10).
 * A failed getProfile does not fan out into record reads unless a raw retry
 * of the same method shows the field was omitted rather than the request failing.
 */
export async function loadBlueskyProfileLinks(input: {
    did: string
    actor: string
    getProfileOk: boolean
    data: Record<string, unknown> | undefined
    profileRecord: Record<string, unknown> | undefined
    agent: Agent
}): Promise<ProfileLink[]> {
    if (!input.did.startsWith('did:')) return []

    const fromViews = (views: unknown): ProfileLink[] =>
        blueskyLinksFromViews(views, input.did) ?? []

    if (input.getProfileOk && input.data && 'links' in input.data) {
        return fromViews(input.data.links)
    }

    const raw = await rawGetProfileLinks(input.agent, input.actor)
    if (raw.kind === 'has') return fromViews(raw.links)
    if (!input.getProfileOk && raw.kind === 'failed') return []
    return linksFromProfileRecord(input.agent, input.did, input.profileRecord)
}

export function mergeAtProfileAccounts(input: {
    bluesky: ProfileLink[]
    mackinac?: { name: string; link: string; icon: string; verifiedAt?: string }[]
}): AtLinkedAccount[] | undefined {
    const merged = mergeProfileLinks({
        bluesky: input.bluesky,
        mackinac: (input.mackinac ?? []).map(account => ({
            name: account.name,
            url: account.link,
            platformIcon: account.icon,
            verifiedAt: account.verifiedAt,
        })),
    })
    if (merged.length === 0) return undefined
    return profileLinksToAccounts(merged)
}
